import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Board from '../components/Board.js';
import { createBoardTask, type Bucket } from '../lib/board.js';
import { ProjectGrid, summarize } from '../components/ProjectCard.js';
import {
  createLifeNote,
  createProject,
  deleteProjectFile,
  KEEPALIVE,
  listProjects,
  updateProject,
  updateProjectFile,
  uploadProjectFile,
} from '../lifeos/api.js';
import { AWAITING, taskTypeLabel } from '../lifeos/agents.js';
import {
  AGENT_LEVEL_LABEL,
  daysBetween,
  dayLabel,
  clock,
  humanize,
  levelLabel,
  prettyHandle,
  plural,
  runCode,
  shortDate,
  ymdInHomeZone,
} from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { options, text } from '../lifeos/forms.js';
import { sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { CopyButton, SafeLink } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import {
  addMeeting,
  cronInWords,
  deleteMeeting,
  getProjectX,
  runJob,
  targetLabel,
  updateDraft,
  updateJob,
  updateProjectX,
  type MeetingRecurrence,
  type ProjectDraft,
  type ProjectDetailX,
  type ProjectJob,
  type ProjectMeeting,
  type ProjectTimelineEntry,
  type ProjectX,
} from '../lib/projectJobs.js';
import { Avatar, Button, ButtonLink, Card, Group, Icon, IconButton, Loader, MoreRow, Page, Row, type IconName } from '../ui/index.js';
import { groupMeetings, type MeetingLine } from '../lib/meetings.js';
import { TimelineRow } from './Timeline.js';
import { slugify } from './Projects.js';
import type { LifeNote, MessageRow, ProjectFile, ProjectKind, ProjectStatus, TimelineItem } from '../lifeos/types.js';

const STATUSES: ProjectStatus[] = ['active', 'paused', 'done', 'archived'];
const PRIORITY_NAME: Record<number, string> = { 1: 'High priority', 2: 'Normal priority', 3: 'Low priority' };

/** Which add/edit sheet is open. */
type SheetState =
  | { kind: 'task' }
  | { kind: 'sub' }
  | { kind: 'note' }
  | { kind: 'meeting' }
  | { kind: 'file'; file: File }
  | { kind: 'editFile'; file: ProjectFile }
  | null;

/** 1536 → '1.5 KB'. */
export function fileSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** From this level up, agents ship without asking: raising the ceiling to it asks first. */
export const SHIPS_ALONE = 3;

/** Runs and timeline rows shown before "Show N more". */
const RUNS_SHOWN = 3;
const RECENT_SHOWN = 6;
/** A note longer than this many characters opens folded behind "Show all". */
export const LONG_NOTE = 600;

/** 'Thu Oct 16 · 2:00 PM'. */
export function meetingWhen(iso: string): string {
  return `${dayLabel(ymdInHomeZone(new Date(iso)))} · ${clock(iso)}`;
}

/**
 * One project (`/projects/:id`, spec 2026-10-14-workspaces), organised so the
 * answer to "what's up?" is visible without scrolling: four tiles (next
 * meeting · due · waiting on · ready) → action items (compact board) → to
 * reply | meetings → a row of folded cards (details, jobs, timeline, people,
 * sub-projects, knowledge base, advanced). Empty sections render nothing; new
 * things are added from the "Add" menu in the header.
 */
export default function Project(): JSX.Element {
  const { id = '' } = useParams();
  const detail = useLoad(() => getProjectX(id), [id]);
  const spheres = useSpheres();
  const toast = useToast();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [ceiling, setCeiling] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [allRuns, setAllRuns] = useState(false);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [goneFiles, setGoneFiles] = useState<ReadonlySet<string>>(new Set());
  const [goneDrafts, setGoneDrafts] = useState<ReadonlySet<string>>(new Set());
  // Fold cards the owner opened or closed this visit (else each card's default).
  const [folds, setFolds] = useState<Record<string, boolean>>({});
  const [focusDraft, setFocusDraft] = useState<{ id: string; n: number } | null>(null);
  const [boardKey, setBoardKey] = useState(0);
  const picker = useRef<HTMLInputElement>(null);
  const draftsRef = useRef<HTMLElement>(null);
  // Candidate parents for the edit sheet.
  const all = useLoad(() => listProjects(), []);
  const today = ymdInHomeZone(new Date());

  function removeFile(projectId: string, f: ProjectFile): void {
    setGoneFiles((s) => new Set(s).add(f.id));
    toast.defer({
      text: `Removed ${f.name}`,
      commit: () => deleteProjectFile(projectId, f.id, KEEPALIVE),
      onUndo: () =>
        setGoneFiles((s) => {
          const n = new Set(s);
          n.delete(f.id);
          return n;
        }),
      onDone: () => void detail.reload(),
      failText: `Couldn't remove ${f.name}`,
    });
  }

  function changeCeiling(p: ProjectX, level: number): void {
    setCeiling(level);
    toast.defer({
      text: `${p.name}: agents may go up to L${level}`,
      commit: () => updateProject(p.id, { autonomyCeiling: level }, KEEPALIVE),
      // Stepping through levels (arrow keys fire change on each) replaces the
      // pending value, so Undo returns to the ceiling the server holds.
      key: `ceiling:${p.id}`,
      onUndo: () => {
        setCeiling(null);
        void detail.reload();
      },
      onDone: () => void detail.reload().finally(() => setCeiling(null)),
      onError: () => setCeiling(null),
      failText: `Couldn't change ${p.name}`,
    });
  }

  async function draftStatus(d: ProjectDraft, status: 'sent' | 'dismissed'): Promise<void> {
    setGoneDrafts((s) => new Set(s).add(d.id));
    try {
      await updateDraft(d.id, { status });
      toast.info(status === 'sent' ? `Marked sent: ${d.title}` : `Dismissed ${d.title}`);
      void detail.reload();
    } catch (err) {
      setGoneDrafts((s) => {
        const n = new Set(s);
        n.delete(d.id);
        return n;
      });
      toast.error(`Couldn't update ${d.title}: ${errorText(err)}`);
    }
  }

  async function removeMeeting(m: ProjectMeeting): Promise<void> {
    try {
      await deleteMeeting(id, m.id);
      toast.info(`Removed ${m.title}`);
      void detail.reload();
    } catch (err) {
      toast.error(`Couldn't remove ${m.title}: ${errorText(err)}`);
    }
  }

  if (!detail.data) {
    return (
      <Page title="" back={{ to: '/projects', label: 'Projects' }}>
        <Loader state={detail}>{() => null}</Loader>
      </Page>
    );
  }
  const d: ProjectDetailX = detail.data;
  const { project: p, children, tasks, runs, timeline, files, notes, meetings, toReply, contacts, jobs } = d;
  const drafts = d.drafts.filter((x) => x.status === 'ready' && !goneDrafts.has(x.id));
  const level = ceiling ?? p.autonomyCeiling;
  const open = tasks.filter((t) => t.status !== 'done' && t.status !== 'dropped');
  const waiting = open.filter((t) => t.status === 'waiting');
  const overdue = open.filter((t) => t.status !== 'waiting' && t.dueOn !== null && t.dueOn < today);
  const shownFiles = files.filter((f) => !goneFiles.has(f.id));
  // Agents matter for sites and apps with a runner; a plain workstream hides the section.
  const showAgents = runs.length > 0 || p.kind === 'client_site' || p.kind === 'app';
  // A project can't sit under itself or anything already under it.
  const descendants = new Set<string>([p.id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const x of all.data ?? []) {
      if (x.parentId && descendants.has(x.parentId) && !descendants.has(x.id)) {
        descendants.add(x.id);
        grew = true;
      }
    }
  }
  const parentOptions = [
    { value: '', label: 'Nothing (top level)' },
    ...(all.data ?? []).filter((x) => !descendants.has(x.id)).map((x) => ({ value: x.id, label: x.name })),
  ];
  const runsShown = allRuns ? runs : runs.slice(0, RUNS_SHOWN);
  // A run waiting on a review keeps Advanced open so it can't hide.
  const awaiting = runs.some((r) => AWAITING.has(r.status) && !!r.approvalId);
  const runner = p.deploy ? [p.deploy.hasBuild && 'build', p.deploy.hasStaging && 'stage', p.deploy.hasProd && 'ship'].filter(Boolean).join(', ') : '';
  const color = sphereColor(spheres, p.sphereKey);
  const nextMeeting = meetings[0];
  const meetingLines = groupMeetings(meetings, today);
  const target = p.targetDate ? targetLabel(p.targetDate, today, shortDate, daysBetween(today, p.targetDate)) : null;
  const targetLate = p.targetDate ? p.targetDate < today && p.status === 'active' : false;
  const kbCount = notes.length + shownFiles.length + (p.notes ? 1 : 0);

  const openMessage = (m: MessageRow): void => navigate(`/inbox?open=${m.id}`, { state: { message: m } });

  const firstWaiting = waiting[0];
  const hasDetails = !!(p.prodUrl || p.stagingUrl || p.hosting || p.repoPath || runner);
  const isOpen = (k: string, def: boolean): boolean => folds[k] ?? def;
  const setFold = (k: string, open: boolean): void => setFolds((f) => (f[k] === open ? f : { ...f, [k]: open }));
  const reviewDrafts = (): void => {
    // One draft: open it right there; several: land on the list.
    if (drafts.length === 1) setFocusDraft({ id: drafts[0]!.id, n: Date.now() });
    else draftsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Page
      title={p.name}
      wide
      className="pj"
      actions={
        <>
          <AddMenu
            items={[
              { label: 'Action item', icon: 'check', onClick: () => setSheet({ kind: 'task' }) },
              { label: 'Meeting', icon: 'calendar', onClick: () => setSheet({ kind: 'meeting' }) },
              { label: 'Sub-project', icon: 'folder', onClick: () => setSheet({ kind: 'sub' }) },
              { label: 'Note', icon: 'book', onClick: () => setSheet({ kind: 'note' }) },
              { label: 'File', icon: 'file', onClick: () => picker.current?.click() },
            ]}
          />
          <IconButton icon="pencil" label="Edit details" onClick={() => setEditing(true)} />
        </>
      }
    >
      <nav className="pj-crumbs" aria-label="Breadcrumb">
        <Link to={`/w/${encodeURIComponent(p.sphereKey)}`}>
          <span className="ui-dot" style={{ background: color }} aria-hidden="true" />
          {sphereName(spheres, p.sphereKey)}
        </Link>
        {d.parent && (
          <>
            <span aria-hidden="true">›</span>
            <Link to={`/projects/${d.parent.id}`}>{d.parent.name}</Link>
          </>
        )}
        <span aria-hidden="true">›</span>
        <span aria-current="page">{p.name}</span>
      </nav>

      <div className="pj-pills">
        <span className={`pj-pill pj-pill--${p.status}`}>{humanize(p.status)}</span>
        {p.priority != null && p.priority !== 2 && <span className={`pj-pill pj-pill--pri${p.priority}`}>{PRIORITY_NAME[p.priority]}</span>}
        {target && <span className={`pj-pill${targetLate ? ' pj-pill--late' : ''}`}>{target}</span>}
        {kindLabel(p.kind) && <span className="pj-pill pj-pill--quiet">{kindLabel(p.kind)}</span>}
      </div>

      <input
        ref={picker}
        type="file"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setSheet({ kind: 'file', file: f });
        }}
      />

      {/* At a glance: always four tiles, so the eye learns where each answer lives. */}
      <div className="pj-now" role="region" aria-label="At a glance">
        {nextMeeting ? (
          <div className="pj-now__tile">
            <span className="pj-now__label">Next meeting</span>
            <span className="pj-now__value">{nextMeeting.title}</span>
            <span className="pj-now__sub">{meetingWhen(nextMeeting.startsAt)}</span>
          </div>
        ) : (
          <button type="button" className="pj-now__tile pj-now__tile--empty" onClick={() => setSheet({ kind: 'meeting' })}>
            <span className="pj-now__label">Next meeting</span>
            <span className="pj-now__value">None scheduled</span>
            <span className="pj-now__sub">Add one →</span>
          </button>
        )}
        {p.targetDate ? (
          <div className={`pj-now__tile${targetLate ? ' pj-now__tile--bad' : ''}`}>
            <span className="pj-now__label">Due</span>
            <span className="pj-now__value">{shortDate(p.targetDate, today)}</span>
            <span className="pj-now__sub">{dueWords(daysBetween(today, p.targetDate))}</span>
          </div>
        ) : (
          <button type="button" className="pj-now__tile pj-now__tile--empty" onClick={() => setEditing(true)}>
            <span className="pj-now__label">Due</span>
            <span className="pj-now__value">No due date</span>
            <span className="pj-now__sub">Set →</span>
          </button>
        )}
        <div className={`pj-now__tile${waiting.length ? ' pj-now__tile--warn' : ''}`}>
          <span className="pj-now__label">Waiting on</span>
          <span className="pj-now__value">{waiting.length ? `${waiting.length} waiting` : 'Nothing'}</span>
          <span className="pj-now__sub">
            {firstWaiting ? `${firstWaiting.title}${firstWaiting.waitingOn ? ` · ${firstWaiting.waitingOn}` : ''}` : 'Not blocked on anyone'}
          </span>
        </div>
        {drafts.length > 0 ? (
          <button type="button" className="pj-now__tile pj-now__tile--accent" onClick={reviewDrafts}>
            <span className="pj-now__label">Ready</span>
            <span className="pj-now__value">{drafts.length === 1 ? `${draftLabel(drafts[0]!)} ready` : `${drafts.length} drafts ready`}</span>
            <span className="pj-now__sub">Review →</span>
          </button>
        ) : (
          <div className="pj-now__tile">
            <span className="pj-now__label">Ready</span>
            <span className="pj-now__value">Nothing to review</span>
            <span className="pj-now__sub">{overdue.length ? `${plural(overdue.length, 'item')} overdue` : 'Drafts land here'}</span>
          </div>
        )}
      </div>

      <section className="pj-sec" aria-label="Action items">
        <h2 className="pj-sec__title">Action items</h2>
        <Board key={boardKey} compact project={p.id} projectSphere={p.sphereKey} hideProjectId={p.id} onChanged={() => void detail.reload()} />
      </section>

      {drafts.length > 0 && (
        <section ref={draftsRef} className="pj-anchor">
          <Section title="Drafts" count={drafts.length}>
            {drafts.map((x) => (
              <DraftCard
                key={x.id}
                draft={x}
                focus={focusDraft?.id === x.id ? focusDraft.n : undefined}
                onStatus={(s) => void draftStatus(x, s)}
                onSaved={() => void detail.reload()}
              />
            ))}
          </Section>
        </section>
      )}

      {(toReply.length > 0 || meetingLines.length > 0) && (
        <div className="pj-two">
          <Section title="To reply" count={toReply.length}>
            <Group label="To reply">
              {toReply.map((m) => (
                <Row
                  key={m.id}
                  leading={<Avatar name={prettyHandle(m.fromName || m.handle)} icon={m.channel === 'email' ? 'mail' : undefined} />}
                  title={prettyHandle(m.fromName || m.handle)}
                  sub={m.subject?.trim() || m.text.slice(0, 140)}
                  detail={shortDate(ymdInHomeZone(new Date(m.sentAt)), today)}
                  onClick={() => openMessage(m)}
                  chevron
                />
              ))}
            </Group>
          </Section>
          <Section title="Meetings" count={meetingLines.length} add={{ label: 'Add', onClick: () => setSheet({ kind: 'meeting' }) }}>
            <Group label="Meetings">
              {meetingLines.map((l) => (
                <MeetingRow key={l.key} line={l} onDelete={() => void removeMeeting(l.next)} />
              ))}
            </Group>
          </Section>
        </div>
      )}

      <div className="pj-folds">
        {hasDetails && (
          <Fold id="details" title="Details" open={isOpen('details', false)} onToggle={setFold}>
            <Group className="pj-details" label="Details">
              {p.prodUrl && (
                <DetailRow label="Live">
                  <SafeLink href={p.prodUrl}>{hostOf(p.prodUrl)}</SafeLink>
                </DetailRow>
              )}
              {p.stagingUrl && (
                <DetailRow label="Staging">
                  <SafeLink href={p.stagingUrl}>{hostOf(p.stagingUrl)}</SafeLink>
                </DetailRow>
              )}
              {p.hosting && <DetailRow label="Hosting">{breakable(p.hosting)}</DetailRow>}
              {p.repoPath && <DetailRow label="Repo">{breakable(p.repoPath)}</DetailRow>}
              {runner && <DetailRow label="Runner can">{runner}</DetailRow>}
              <DetailRow label="Short name">{p.slug}</DetailRow>
            </Group>
          </Fold>
        )}

        {jobs.length > 0 && (
          <Fold id="jobs" title="Recurring jobs" count={jobs.length} open={isOpen('jobs', false)} onToggle={setFold}>
            <Group label="Recurring jobs">
              {jobs.map((j) => (
                <JobRow key={j.id} job={j} projectId={j.projectId || p.id} onChanged={() => void detail.reload()} />
              ))}
            </Group>
          </Fold>
        )}

        {timeline.length > 0 && (
          <Fold id="timeline" title="Timeline" count={timeline.length} open={isOpen('timeline', false)} onToggle={setFold}>
            <RecentTimeline items={timeline} today={today} />
          </Fold>
        )}

        {contacts.length > 0 && (
          <Fold id="people" title="People" count={contacts.length} open={isOpen('people', false)} onToggle={setFold}>
            <Group label="People">
              {contacts.map((c) => (
                <Row
                  key={c.id}
                  to={`/people/${c.id}`}
                  leading={<Avatar name={c.name} />}
                  title={c.name}
                  sub={
                    (c.tags?.length ?? 0) > 0 ? (
                      <span className="pj-tags">
                        {c.tags!.map((t) => (
                          <span key={t} className={`tag-chip tag-chip--${t}`}>
                            {t}
                          </span>
                        ))}
                      </span>
                    ) : (
                      c.role ?? undefined
                    )
                  }
                />
              ))}
            </Group>
          </Fold>
        )}

        {children.length > 0 && (
          <Fold id="subs" title="Sub-projects" count={children.length} open={isOpen('subs', true)} onToggle={setFold} wide>
            <ProjectGrid projects={summarize(children)} color={color} />
          </Fold>
        )}

        {kbCount > 0 && (
          <Fold
            id="kb"
            title="Knowledge base"
            hint={[notes.length + (p.notes ? 1 : 0) ? plural(notes.length + (p.notes ? 1 : 0), 'note') : null, shownFiles.length ? plural(shownFiles.length, 'file') : null]
              .filter(Boolean)
              .join(' · ')}
            open={isOpen('kb', false)}
            onToggle={setFold}
            wide
          >
            <div className="pj-notes">
              {p.notes && <NoteCard title="Project notes" body={p.notes} />}
              {notes.map((n) => (
                <NoteCard key={n.id} title={n.title} body={n.body} note={n} today={today} />
              ))}
            </div>
            {shownFiles.length > 0 && (
              <Group header="Files" label="Files">
                {shownFiles.map((f) => (
                  <Row
                    key={f.id}
                    leading={<Icon name="file" size={20} />}
                    title={f.name}
                    sub={[f.about, fileSize(f.sizeBytes)].filter(Boolean).join(' · ')}
                    onClick={() => window.open(`/api/files/${encodeURIComponent(f.fileId)}`, '_blank', 'noopener')}
                    chevron={false}
                    trailing={<IconButton icon="pencil" label={`Rename or describe ${f.name}`} onClick={() => setSheet({ kind: 'editFile', file: f })} />}
                    swipe={{ trailing: [{ label: 'Remove', icon: 'trash', tone: 'bad', onAction: () => removeFile(p.id, f) }] }}
                  />
                ))}
              </Group>
            )}
          </Fold>
        )}

        {(showAgents || runs.length > 0) && (
          <Fold id="adv" title="Advanced" hint={`Agents${runs.length ? ` · ${plural(runs.length, 'run')}` : ''}`} open={isOpen('adv', awaiting)} onToggle={setFold}>
            {showAgents && (
              <Group header="Agents" footer={AGENT_LEVEL_LABEL[confirming ?? level]}>
                <li className="ui-swipe">
                  <label className="ui-row la-select lo-ceiling">
                    <span className="ui-row__main">
                      <span className="ui-row__title">Autonomy ceiling</span>
                    </span>
                    <select
                      value={confirming ?? level}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        // Letting agents ship on their own is the one change worth a second look.
                        if (next >= SHIPS_ALONE && next > level) setConfirming(next);
                        else {
                          setConfirming(null);
                          changeCeiling(p, next);
                        }
                      }}
                      aria-label="Agent autonomy ceiling"
                    >
                      {AGENT_LEVEL_LABEL.map((label, l) => (
                        <option key={l} value={l}>
                          L{l} · {label.split(' — ')[0]}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              </Group>
            )}
            {confirming !== null && (
              <Card
                className="lo-confirm"
                accent="var(--bad)"
                eyebrow="Check this"
                label="Confirm autonomy"
                actions={
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        const next = confirming;
                        setConfirming(null);
                        changeCeiling(p, next);
                      }}
                    >
                      Raise to L{confirming}
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirming(null)}>
                      Keep L{level}
                    </Button>
                  </>
                }
              >
                <p className="la-confirm__text" role="alert">
                  At {levelLabel(confirming)}, agents may ship changes to {p.name} without asking you first, unless a hard stop applies.
                </p>
              </Card>
            )}

            {runs.length > 0 && (
              <Group header="Agent runs">
                {runsShown.map((r) => (
                  <Row
                    key={r.id}
                    to={`/agents/runs/${r.id}`}
                    title={
                      <>
                        <span className="la-code">{runCode(r.shortCode)}</span> {taskTypeLabel(r.taskType)}
                      </>
                    }
                    sub={humanize(r.status)}
                    chevron={!(AWAITING.has(r.status) && r.approvalId)}
                    trailing={
                      AWAITING.has(r.status) && r.approvalId ? (
                        <ButtonLink size="sm" variant="secondary" to={`/agents?approval=${encodeURIComponent(r.approvalId)}`}>
                          Review
                        </ButtonLink>
                      ) : undefined
                    }
                  />
                ))}
                {runs.length > runsShown.length && <MoreRow count={runs.length - runsShown.length} onClick={() => setAllRuns(true)} />}
              </Group>
            )}
          </Fold>
        )}
      </div>

      {sheet?.kind === 'task' && (
        <FormSheet
          title="Add action item"
          submitLabel="Add"
          fields={[
            { key: 'title', label: 'What', required: true, wide: true, placeholder: 'e.g. Send Tyler the logo files' },
            {
              key: 'bucket',
              label: 'When',
              kind: 'select',
              options: [
                { value: 'this_week', label: 'This week' },
                { value: 'next', label: 'Next' },
                { value: 'later', label: 'Later' },
              ],
            },
          ]}
          initial={{ bucket: 'this_week' }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            await createBoardTask({ title: text(v.title)!, bucket: (v.bucket || 'this_week') as Bucket, status: 'next', sphereKey: p.sphereKey, projectId: p.id });
            toast.info('Added');
            setBoardKey((k) => k + 1);
            void detail.reload();
          }}
        />
      )}
      {editing && (
        <FormSheet
          title={`Edit ${p.name}`}
          fields={[
            { key: 'name', label: 'Name', required: true, wide: true },
            { key: 'status', label: 'Status', kind: 'select', options: options(STATUSES, humanize) },
            {
              key: 'priority',
              label: 'Priority',
              kind: 'select',
              options: [
                { value: '1', label: 'High' },
                { value: '2', label: 'Normal' },
                { value: '3', label: 'Low' },
              ],
            },
            { key: 'targetDate', label: 'Target date', kind: 'date' },
            { key: 'sphereKey', label: 'Workspace', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
            { key: 'parentId', label: 'Sits under', kind: 'select', options: parentOptions },
            { key: 'prodUrl', label: 'Live site', wide: true },
            { key: 'stagingUrl', label: 'Staging site', wide: true },
            { key: 'hosting', label: 'Hosting' },
            { key: 'notes', label: 'Notes', kind: 'textarea' },
          ]}
          initial={{
            name: p.name,
            status: p.status,
            priority: String(p.priority ?? 2),
            targetDate: p.targetDate ?? '',
            sphereKey: p.sphereKey,
            parentId: p.parentId ?? '',
            prodUrl: p.prodUrl ?? '',
            stagingUrl: p.stagingUrl ?? '',
            hosting: p.hosting ?? '',
            notes: p.notes ?? '',
          }}
          onClose={() => setEditing(false)}
          onSubmit={async (v) => {
            await updateProjectX(p.id, {
              name: text(v.name)!,
              status: v.status as ProjectStatus,
              priority: Number(v.priority) || 2,
              targetDate: text(v.targetDate),
              sphereKey: v.sphereKey || undefined,
              parentId: v.parentId || null,
              prodUrl: text(v.prodUrl),
              stagingUrl: text(v.stagingUrl),
              hosting: text(v.hosting),
              notes: text(v.notes),
            });
            toast.info(`Saved ${p.name}`);
            void detail.reload();
            setBoardKey((k) => k + 1);
          }}
        />
      )}

      {sheet?.kind === 'sub' && (
        <FormSheet
          title={`Sub-project of ${p.name}`}
          submitLabel="Add"
          fields={[{ key: 'name', label: 'Name', required: true, wide: true, placeholder: 'e.g. Stripe sync' }]}
          initial={{}}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            const name = text(v.name)!;
            const c = await createProject({
              name,
              slug: slugify(`${p.slug}-${name}`),
              kind: 'internal' as ProjectKind,
              sphereKey: p.sphereKey,
              parentId: p.id,
            });
            toast.info(`Added ${c.name}`);
            void detail.reload();
          }}
        />
      )}
      {sheet?.kind === 'meeting' && (
        <FormSheet
          title="Add meeting"
          submitLabel="Add"
          fields={[
            { key: 'title', label: 'What', required: true, wide: true, placeholder: 'e.g. Weekly check-in with Tyler' },
            { key: 'startsAt', label: 'When', kind: 'datetime', required: true },
            {
              key: 'durationMin',
              label: 'Length',
              kind: 'select',
              options: [
                { value: '15', label: '15 min' },
                { value: '30', label: '30 min' },
                { value: '45', label: '45 min' },
                { value: '60', label: '1 hour' },
                { value: '90', label: '1½ hours' },
              ],
            },
            {
              key: 'recurrence',
              label: 'Repeats',
              kind: 'select',
              options: [
                { value: 'none', label: 'Does not repeat' },
                { value: 'weekly', label: 'Every week' },
                { value: 'monthly', label: 'Every month' },
              ],
            },
            { key: 'location', label: 'Where', wide: true, placeholder: 'optional — Zoom link, address…' },
          ]}
          initial={{ durationMin: '30', recurrence: 'none' }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            const at = v.startsAt ? new Date(v.startsAt) : null;
            if (!at || Number.isNaN(at.getTime())) throw new Error('Pick a date and time.');
            await addMeeting(p.id, {
              title: text(v.title)!,
              startsAt: at.toISOString(),
              durationMin: Number(v.durationMin) || 30,
              recurrence: (v.recurrence || 'none') as MeetingRecurrence,
              ...(text(v.location) ? { location: text(v.location)! } : {}),
            });
            toast.info('Meeting added');
            void detail.reload();
          }}
        />
      )}
      {sheet?.kind === 'note' && (
        <FormSheet
          title="Add a note"
          submitLabel="Add"
          fields={[
            { key: 'title', label: 'Title', wide: true },
            { key: 'body', label: 'Note', kind: 'textarea', required: true },
          ]}
          initial={{}}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            await createLifeNote({ kind: 'reference', title: text(v.title), body: text(v.body)!, projectId: p.id, sphereKey: p.sphereKey });
            void detail.reload();
          }}
        />
      )}
      {sheet?.kind === 'file' && (
        <FormSheet
          title="Upload a file"
          submitLabel="Upload"
          fields={[
            { key: 'name', label: 'Name', required: true, wide: true, hint: sheet.file.name },
            { key: 'about', label: 'What is it?', kind: 'textarea', placeholder: 'e.g. Signed SOW from ShareWELL, v2' },
          ]}
          initial={{ name: sheet.file.name.replace(/\.[^.]{1,5}$/, '') }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            await uploadProjectFile(p.id, sheet.file, { name: text(v.name) ?? undefined, about: text(v.about) ?? undefined });
            setFolds((f) => ({ ...f, kb: true }));
            void detail.reload();
          }}
        />
      )}
      {sheet?.kind === 'editFile' && (
        <FormSheet
          title={`Edit ${sheet.file.name}`}
          fields={[
            { key: 'name', label: 'Name', required: true, wide: true, hint: sheet.file.originalName ?? undefined },
            { key: 'about', label: 'What is it?', kind: 'textarea' },
          ]}
          initial={{ name: sheet.file.name, about: sheet.file.about ?? '' }}
          danger={{
            label: 'Remove file',
            onClick: () => {
              const f = sheet.file;
              setSheet(null);
              removeFile(p.id, f);
            },
          }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            await updateProjectFile(p.id, sheet.file.id, { name: text(v.name)!, about: text(v.about) ?? null });
            void detail.reload();
          }}
        />
      )}
    </Page>
  );
}

/** A titled block; nothing at all when it has nothing (adding lives in the header's Add menu). */
function Section({
  title,
  count,
  add,
  children,
}: {
  title: string;
  count: number;
  add?: { label: string; onClick: () => void };
  children?: ReactNode;
}): JSX.Element | null {
  if (count === 0) return null;
  return (
    <section className="pj-sec" aria-label={title}>
      <div className="pj-sec__head">
        <h2 className="pj-sec__title">
          {title} <span className="pj-sec__count">{count}</span>
        </h2>
        {add && (
          <button type="button" className="ui-group__action" onClick={add.onClick} aria-label={`${add.label} ${title.toLowerCase()}`}>
            {add.label}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

/** A folded card in the bottom row: title, count or hint, chevron; native <details> so it stays in the page's find-in-page. */
function Fold({
  id,
  title,
  count,
  hint,
  open,
  wide = false,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  count?: number;
  hint?: string;
  open: boolean;
  /** Spans the whole row when open (grids of cards). */
  wide?: boolean;
  onToggle: (id: string, open: boolean) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <details
      className={`pj-fold${wide ? ' pj-fold--wide' : ''}`}
      open={open}
      onToggle={(e) => onToggle(id, e.currentTarget.open)}
      aria-label={title}
      role="group"
    >
      <summary className="pj-fold__sum">
        <span className="pj-fold__title">{title}</span>
        {count !== undefined && <span className="pj-fold__count">{count}</span>}
        {hint && <span className="pj-fold__hint">{hint}</span>}
        <Icon name="chevron-right" size={16} className="pj-fold__chev" />
      </summary>
      <div className="pj-fold__body">{children}</div>
    </details>
  );
}

/** "Add" in the page header: one menu for everything a project can gain. */
function AddMenu({ items }: { items: { label: string; icon: IconName; onClick: () => void }[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="pj-addmenu" ref={box}>
      <Button size="sm" icon="plus" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Add
      </Button>
      {open && (
        <div className="pj-addmenu__list" role="menu" aria-label="Add to project">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className="pj-addmenu__item"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              <Icon name={it.icon} size={18} />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** 12 → '12 days left'; 0 → 'Today'; -3 → '3 days late'. */
export function dueWords(days: number): string {
  if (days === 0) return 'Due today';
  if (days === 1) return 'Tomorrow';
  if (days > 0) return `${days} days left`;
  return days === -1 ? '1 day late' : `${-days} days late`;
}

/** One row per meeting series ("Tuesdays 12:30 PM · next Oct 20") or a dated one-off. */
function MeetingRow({ line, onDelete }: { line: MeetingLine<ProjectMeeting>; onDelete: () => void }): JSX.Element {
  const m = line.next;
  return (
    <Row
      leading={<Icon name={line.series ? 'repeat' : 'calendar'} size={20} />}
      title={m.title}
      sub={[line.when, m.durationMin ? `${m.durationMin} min` : null].filter(Boolean).join(' · ')}
      swipe={{ trailing: [{ label: 'Remove', icon: 'trash', tone: 'bad', onAction: onDelete }] }}
    />
  );
}

const KIND_LABEL: Record<string, string> = { client_site: 'Client site', app: 'App', integration: 'Integration', internal: 'Internal', personal: 'Personal' };

/** The project-kind pill: humanized, and nothing at all for 'other'. */
export function kindLabel(kind: string | null | undefined): string | null {
  if (!kind || kind === 'other') return null;
  return KIND_LABEL[kind] ?? humanize(kind);
}

/** 'https://www.fergusonsgaragedoor.com/contact' → 'fergusonsgaragedoor.com'. */
export function hostOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '') || href;
  } catch {
    return href.replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0] || href;
  }
}

/** A path or long token that may wrap after each '/', '.', '-' or '_' instead of mid-word. */
function breakable(text: string): ReactNode {
  const parts = text.split(/(?<=[/._-])/);
  return parts.length < 2 ? text : parts.map((x, i) => (i ? [<wbr key={i} />, x] : x));
}

/** One details line: the label small above, the value below, left-aligned. */
function DetailRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return <Row className="pj-fact" title={<span className="pj-fact__label">{label}</span>} sub={<span className="pj-fact__value">{children}</span>} />;
}

/** "Weekly SEO brief" for a sub-project's draft, else the draft's own title. */
function draftLabel(d: ProjectDraft): string {
  return d.projectName || d.title;
}

/** One recurring job: what, when in words, how the last run went, Run now, on/off. */
function JobRow({ job, projectId, onChanged }: { job: ProjectJob; projectId: string; onChanged: () => void }): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(job.enabled);
  const failed = job.lastStatus && /^(error|failed|fail)$/i.test(job.lastStatus);
  const last = job.lastRunAt
    ? `Last run ${shortDate(ymdInHomeZone(new Date(job.lastRunAt)))}${job.lastStatus ? ` · ${humanize(job.lastStatus)}` : ''}`
    : 'Not run yet';
  async function run(): Promise<void> {
    setBusy(true);
    try {
      const r = await runJob(projectId, job.id);
      toast.info(r.draft ? `${r.draft.title} is ready` : `${job.title} ran`);
      onChanged();
    } catch (err) {
      toast.error(`Couldn't run ${job.title}: ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  }
  async function toggle(next: boolean): Promise<void> {
    setEnabled(next);
    try {
      await updateJob(projectId, job.id, { enabled: next });
      onChanged();
    } catch (err) {
      setEnabled(!next);
      toast.error(`Couldn't change ${job.title}: ${errorText(err)}`);
    }
  }
  return (
    <Row
      className="pj-job"
      leading={<Icon name="repeat" size={20} />}
      title={job.title}
      sub={
        <>
          <span>{job.projectName ? `${job.projectName} · ${cronInWords(job.schedule)}` : cronInWords(job.schedule)}</span>
          <span className={`pj-job__last${failed ? ' pj-job__last--bad' : ''}`} title={job.lastError ?? undefined}>
            {last}
          </span>
        </>
      }
      trailing={
        <>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run()}>
            {busy ? 'Running…' : 'Run now'}
          </Button>
          <label className="pj-switch" title={enabled ? 'On' : 'Off'}>
            <input type="checkbox" role="switch" checked={enabled} aria-label={`${job.title} on`} onChange={(e) => void toggle(e.target.checked)} />
            <span aria-hidden="true" />
          </label>
        </>
      }
    />
  );
}

/** A ready draft: title, the body folded, then Copy / Edit / Mark sent / Dismiss. */
function DraftCard({
  draft,
  focus,
  onStatus,
  onSaved,
}: {
  draft: ProjectDraft;
  /** Changes when "Right now → Review" asks for this draft: open it and scroll to it. */
  focus?: number;
  onStatus: (s: 'sent' | 'dismissed') => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const self = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focus === undefined) return;
    setOpen(true);
    self.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [focus]);
  const [edit, setEdit] = useState<string | null>(null);
  const [body, setBody] = useState(draft.body);
  const [busy, setBusy] = useState(false);
  async function save(): Promise<void> {
    if (edit === null) return;
    setBusy(true);
    try {
      await updateDraft(draft.id, { body: edit });
      setBody(edit);
      setEdit(null);
      toast.info('Draft saved');
      onSaved();
    } catch (err) {
      toast.error(`Couldn't save: ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    <article ref={self} className="pj-draft" aria-label={draft.title}>
      <button type="button" className="pj-draft__head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="pj-draft__title">
          {draft.title}
          {draft.projectName && <span className="pj-draft__from">{draft.projectName}</span>}
        </span>
        <span className="pj-draft__when">{shortDate(ymdInHomeZone(new Date(draft.createdAt)))}</span>
        <Icon name="chevron-right" size={18} className="pj-draft__chev" />
      </button>
      {open &&
        (edit !== null ? (
          <textarea className="settings-input pj-draft__edit" rows={14} value={edit} aria-label={`Edit ${draft.title}`} onChange={(e) => setEdit(e.target.value)} />
        ) : (
          <pre className="pj-long pj-long--open">{body}</pre>
        ))}
      <div className="pj-draft__actions">
        {edit !== null ? (
          <>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEdit(null)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            {!open && (
              <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
                Review
              </Button>
            )}
            <CopyButton text={body} />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setOpen(true);
                setEdit(body);
              }}
            >
              Edit
            </Button>
            <Button size="sm" onClick={() => onStatus('sent')}>
              Mark sent
            </Button>
            <Button size="sm" variant="plain" onClick={() => onStatus('dismissed')}>
              Dismiss
            </Button>
          </>
        )}
      </div>
    </article>
  );
}

/** A knowledge-base note: title + two-line preview; tap to read; long ones fold behind "Show all". */
export function NoteCard({ title, body, note, today }: { title: string | null; body: string; note?: LifeNote; today?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const long = body.length > LONG_NOTE || body.split('\n').length > 14;
  const obsidian = note?.source === 'obsidian';
  return (
    <article className={`pj-note${open ? ' pj-note--open' : ''}`}>
      <button type="button" className="pj-note__head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="pj-note__title">
          {title || body.split('\n')[0]!.slice(0, 80)}
          {obsidian && <span className="notes__src">Obsidian</span>}
        </span>
        {!open && <span className="pj-note__preview">{body}</span>}
        {note && today && <span className="pj-note__when">{shortDate(ymdInHomeZone(new Date(note.updatedAt ?? note.createdAt)), today)}</span>}
      </button>
      {open && (
        <>
          <pre className={`pj-long${full || !long ? ' pj-long--open' : ''}`}>{body}</pre>
          {long && (
            <button type="button" className="pj-note__more" onClick={() => setFull((f) => !f)}>
              {full ? 'Show less' : 'Show all'}
            </button>
          )}
          {obsidian && note?.externalRef && <p className="pj-note__src">Read-only · {note.externalRef}</p>}
        </>
      )}
    </article>
  );
}

const TL_KIND: Record<string, string> = { done: 'Done', message: 'Message', note: 'Note', file: 'File', draft: 'Draft' };

/** Last 30 days, compact: the full rows from an older server, or the new {at, kind, title} rows. */
function RecentTimeline({ items, today }: { items: readonly (TimelineItem | ProjectTimelineEntry)[]; today: string }): JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, RECENT_SHOWN);
  return (
    <Group label="Timeline" className="pj-tl">
      {shown.map((it, i) => {
        const when = shortDate(ymdInHomeZone(new Date(it.at)), today);
        if ('refTable' in it) return <TimelineRow key={`${it.refTable}:${it.refId}`} it={it} when={when} />;
        return <Row key={`${it.at}:${i}`} className="la-tl" title={it.title ?? TL_KIND[it.kind] ?? humanize(it.kind)} sub={`${TL_KIND[it.kind] ?? humanize(it.kind)} · ${when}`} />;
      })}
      {items.length > shown.length && <MoreRow count={items.length - shown.length} onClick={() => setAll(true)} />}
    </Group>
  );
}
